// central.eu — landing view. Full v2 design as a dees-element component:
// styles live in static styles (themed via :host token vars), lucide icons are
// built from real icon data, and the two maplibre maps render into shadow DOM.

import {
  DeesElement,
  customElement,
  html,
  css,
  state,
  cssManager,
  type TemplateResult,
} from '@design.estate/dees-element';
import { tokenHostVars, baseStyles } from '../ceu-theme.js';
import { icon } from '../ceu-icons.js';
import { DCS, EU_ISO, TICKER_ITEMS, CATALOG, mapColors } from '../ceu-data.js';

@customElement('ceu-landing')
export class CeuLanding extends DeesElement {
  @state() accessor estCores = 4;
  @state() accessor estMem = 8;
  @state() accessor estBlk = 50;
  @state() accessor estObj = 100;
  @state() accessor estCoreType: 'shared' | 'dedicated' = 'shared';

  private heroMap: any;
  private footMap: any;

  public static styles = [
    cssManager.defaultStyles,
    tokenHostVars,
    baseStyles,
    css`
      :host {
        display: block;
        font-size: 15px;
        line-height: 1.5;
        letter-spacing: -0.01em;
        font-family: var(--ff-text);
        background: var(--canvas);
        color: var(--ink);
      }
      .label {
        font-family: var(--ff-mono);
        font-size: 11px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--muted);
      }
      .ic-tile {
        width: 44px;
        height: 44px;
        border-radius: 12px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: color-mix(in oklab, var(--eu) 12%, transparent);
        color: var(--eu);
        flex-shrink: 0;
      }
      .ic-tile .lucide {
        width: 22px;
        height: 22px;
      }
      .section-inner {
        max-width: var(--maxw);
        margin: 0 auto;
        padding: 88px var(--pad-x);
      }
      .section-head {
        display: grid;
        grid-template-columns: 200px 1fr;
        gap: 32px;
        padding-bottom: 40px;
        align-items: baseline;
      }
      .section-head .index {
        font-family: var(--ff-mono);
        font-size: 11px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--muted);
      }
      .section-head h2 {
        margin: 0;
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: clamp(28px, 3.4vw, 46px);
        line-height: 1.04;
        letter-spacing: -0.03em;
      }
      .section-head p.lede {
        margin: 18px 0 0;
        color: var(--muted);
        max-width: 58ch;
        font-size: 16px;
        line-height: 1.55;
      }
      @media (max-width: 720px) {
        .section-head {
          grid-template-columns: 1fr;
          gap: 16px;
        }
      }

      /* Nav */
      .nav {
        position: sticky;
        top: 0;
        z-index: 50;
        background: color-mix(in oklab, var(--canvas) 72%, transparent);
        backdrop-filter: saturate(1.8) blur(20px);
        -webkit-backdrop-filter: saturate(1.8) blur(20px);
        border-bottom: 1px solid var(--hairline-2);
      }
      .nav-inner {
        max-width: var(--maxw);
        margin: 0 auto;
        display: flex;
        align-items: center;
        gap: 30px;
        padding: 13px var(--pad-x);
      }
      .brand {
        display: flex;
        align-items: baseline;
        gap: 9px;
        font-family: var(--ff-display);
        font-weight: 600;
        letter-spacing: -0.02em;
        font-size: 18px;
      }
      .brand .dot {
        width: 8px;
        height: 8px;
        background: var(--eu);
        display: inline-block;
        border-radius: 50%;
        transform: translateY(-2px);
      }
      .brand .tld {
        color: var(--muted);
        font-weight: 400;
      }
      .nav-links {
        display: flex;
        gap: 26px;
        margin-left: 6px;
      }
      .nav-links a {
        font-size: 14px;
        font-weight: 450;
        color: var(--ink-2);
        transition: color 0.15s ease;
      }
      .nav-links a:hover {
        color: var(--ink);
      }
      .nav-right {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 14px;
      }
      .nav-right a,
      .nav-right .btn {
        white-space: nowrap;
      }
      .status {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-family: var(--ff-mono);
        font-size: 12px;
        color: var(--muted);
      }
      .status::before {
        content: '';
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--green);
        box-shadow: 0 0 0 3px color-mix(in oklab, var(--green) 22%, transparent);
      }
      @media (max-width: 1240px) {
        .status .status-text {
          display: none;
        }
      }
      @media (max-width: 1080px) {
        .nav-inner {
          gap: 20px;
        }
        .nav-links {
          gap: 18px;
          overflow-x: auto;
          scrollbar-width: none;
        }
      }
      @media (max-width: 760px) {
        .nav-links,
        .signin {
          display: none;
        }
      }
      .nav-toggle {
        width: 34px;
        height: 34px;
        border-radius: 50%;
        border: 1px solid var(--hairline-2);
        background: var(--card);
        color: var(--ink-2);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s ease, color 0.15s ease, transform 0.12s ease;
      }
      .nav-toggle:hover {
        background: var(--panel-2);
        color: var(--ink);
      }
      .nav-toggle:active {
        transform: scale(0.92);
      }
      .nav-toggle .lucide {
        width: 16px;
        height: 16px;
      }
      .btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 11px 19px;
        font-family: var(--ff-display);
        font-size: 14px;
        font-weight: 600;
        letter-spacing: -0.01em;
        background: var(--eu-brand);
        color: #fff;
        border-radius: var(--pill);
        transition: filter 0.15s ease, transform 0.12s ease;
      }
      .btn:hover {
        filter: brightness(1.08);
      }
      .btn:active {
        transform: scale(0.97);
      }
      .btn.ghost {
        background: var(--panel-2);
        color: var(--ink);
      }
      .btn .lucide {
        width: 16px;
        height: 16px;
      }

      /* Hero */
      .hero {
        position: relative;
        overflow: hidden;
        min-height: 620px;
        display: flex;
        align-items: stretch;
      }
      .hero-grid {
        padding: 120px var(--pad-x);
        padding-left: max(var(--pad-x), calc((100vw - var(--maxw)) / 2 + var(--pad-x)));
        width: 50%;
        position: relative;
        z-index: 1;
        flex-shrink: 0;
      }
      @media (max-width: 900px) {
        .hero-grid {
          width: 100%;
        }
      }
      .hero-map-bg {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 42%;
        z-index: 0;
        -webkit-mask-image: linear-gradient(to right, transparent 0%, black 22%, black 100%);
        mask-image: linear-gradient(to right, transparent 0%, black 22%, black 100%);
      }
      @media (max-width: 900px) {
        .hero-map-bg {
          display: none;
        }
      }
      .hero h1 {
        margin: 0;
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: clamp(46px, 7vw, 92px);
        line-height: 0.98;
        letter-spacing: -0.04em;
      }
      .hero .stripe {
        color: var(--eu);
      }
      .hero-lede {
        margin-top: 24px;
        font-size: 18px;
        line-height: 1.5;
        color: var(--ink-2);
        max-width: 46ch;
      }
      .hero-cta {
        margin-top: 32px;
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
      }
      .hero-cta .btn {
        padding: 13px 22px;
        font-size: 15px;
      }
      .hero-meta {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 10px;
        margin-top: 40px;
      }
      @media (max-width: 1020px) {
        .hero-meta {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      .hero-meta .kv {
        display: flex;
        flex-direction: column;
        gap: 3px;
        font-family: var(--ff-mono);
        font-size: 12px;
        background: color-mix(in oklab, var(--card) 60%, transparent);
        border: 1px solid var(--hairline-2);
        border-radius: var(--radius-xs);
        padding: 10px 12px;
        backdrop-filter: blur(6px);
      }
      .hero-meta .kv .k {
        color: var(--muted);
        font-size: 10px;
        letter-spacing: 0.04em;
      }
      .hero-meta .kv .v {
        color: var(--ink);
      }

      /* Ticker */
      .ticker {
        border-top: 1px solid var(--hairline-2);
        border-bottom: 1px solid var(--hairline-2);
        overflow: hidden;
        background: var(--card);
      }
      .ticker-track {
        display: flex;
        gap: 44px;
        padding: 13px 0;
        white-space: nowrap;
        animation: tick 60s linear infinite;
        font-family: var(--ff-mono);
        font-size: 12px;
        color: var(--muted);
        letter-spacing: 0.02em;
      }
      .ticker-track span.sep {
        opacity: 0.35;
      }
      .ticker-track span.on {
        color: var(--eu);
        font-weight: 500;
      }
      @keyframes tick {
        from {
          transform: translateX(0);
        }
        to {
          transform: translateX(-50%);
        }
      }

      /* Values */
      .values {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 16px;
        max-width: var(--maxw);
        margin: 0 auto;
        padding: 0 var(--pad-x);
      }
      @media (max-width: 860px) {
        .values {
          grid-template-columns: 1fr;
        }
      }
      .value {
        padding: 32px;
        background: var(--card);
        border: 1px solid var(--hairline-2);
        border-radius: var(--radius);
        box-shadow: var(--shadow-sm);
        min-height: 280px;
        display: flex;
        flex-direction: column;
        gap: 14px;
      }
      .value .idx {
        font-family: var(--ff-mono);
        font-size: 11px;
        color: var(--muted);
        letter-spacing: 0.04em;
      }
      .value h3 {
        margin: 0;
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: 22px;
        line-height: 1.15;
        letter-spacing: -0.02em;
      }
      .value p {
        margin: 0;
        color: var(--ink-2);
        font-size: 14.5px;
        line-height: 1.55;
      }
      .value .glyph {
        margin-top: auto;
        display: flex;
        align-items: flex-end;
        color: var(--ink);
        opacity: 0.9;
      }
      .value .glyph svg {
        width: 100%;
        height: auto;
        display: block;
      }

      /* Map / footprint */
      .map-wrap {
        display: grid;
        grid-template-columns: 1.5fr 1fr;
        border: 1px solid var(--hairline-2);
        border-radius: var(--radius);
        box-shadow: var(--shadow-sm);
        background: var(--card);
        min-height: 560px;
        overflow: hidden;
      }
      @media (max-width: 960px) {
        .map-wrap {
          grid-template-columns: 1fr;
        }
      }
      .map-svg {
        position: relative;
        background: var(--card);
        min-height: 500px;
        height: 100%;
      }
      .map-marker {
        cursor: pointer;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
      }
      .map-marker::before {
        content: '';
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: var(--eu);
        border: 2px solid var(--card);
        box-shadow: 0 0 0 1.5px var(--eu), 0 1px 4px rgba(0, 0, 0, 0.25);
        flex-shrink: 0;
        transition: box-shadow 0.15s;
      }
      .map-marker.build::before {
        background: var(--amber);
        box-shadow: 0 0 0 1.5px var(--amber), 0 1px 4px rgba(0, 0, 0, 0.25);
      }
      .map-marker .map-label {
        font-family: var(--ff-mono);
        font-size: 9px;
        color: var(--ink-2);
        white-space: nowrap;
        margin-left: 6px;
        background: color-mix(in oklab, var(--card) 86%, transparent);
        padding: 2px 5px;
        border-radius: var(--pill);
        pointer-events: none;
      }
      .map-marker:hover::before,
      .map-marker.active::before {
        box-shadow: 0 0 0 4px color-mix(in oklab, var(--eu) 28%, transparent);
      }
      .map-marker.build:hover::before,
      .map-marker.build.active::before {
        box-shadow: 0 0 0 4px color-mix(in oklab, var(--amber) 28%, transparent);
      }
      .map-side {
        border-left: 1px solid var(--hairline-2);
        padding: 26px;
        display: flex;
        flex-direction: column;
        gap: 16px;
      }
      @media (max-width: 960px) {
        .map-side {
          border-left: 0;
          border-top: 1px solid var(--hairline-2);
        }
      }
      .map-side h3 {
        margin: 0;
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: 19px;
        letter-spacing: -0.02em;
      }
      .map-side .summary {
        font-family: var(--ff-mono);
        font-size: 12px;
        color: var(--muted);
        line-height: 1.6;
      }
      .dc-list {
        display: flex;
        flex-direction: column;
      }
      .dc-row {
        display: grid;
        grid-template-columns: 46px 1fr auto;
        gap: 12px;
        padding: 12px 8px;
        margin-inline: -8px;
        border-top: 1px solid var(--hairline-2);
        border-radius: 8px;
        font-family: var(--ff-mono);
        font-size: 12px;
        align-items: center;
        cursor: pointer;
      }
      .dc-row:last-child {
        border-bottom: 1px solid var(--hairline-2);
      }
      .dc-row .code {
        color: var(--muted);
      }
      .dc-row .name {
        color: var(--ink);
      }
      .dc-row .name .sub {
        color: var(--muted);
        margin-left: 8px;
        font-size: 11px;
      }
      .dc-row .pill {
        font-size: 10px;
        padding: 3px 9px;
        border-radius: var(--pill);
        color: var(--muted);
        letter-spacing: 0.02em;
        white-space: nowrap;
        background: var(--panel-2);
      }
      .dc-row[data-state='live'] .pill {
        color: var(--green);
        background: color-mix(in oklab, var(--green) 14%, transparent);
      }
      .dc-row[data-state='build'] .pill {
        color: var(--amber);
        background: color-mix(in oklab, var(--amber) 14%, transparent);
      }
      .dc-row:hover,
      .dc-row.active {
        background: var(--panel-2);
      }

      /* Products */
      .products {
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        gap: 16px;
        max-width: var(--maxw);
        margin: 0 auto;
        padding: 0 var(--pad-x);
      }
      .product {
        grid-column: span 4;
        padding: 32px;
        background: var(--card);
        border: 1px solid var(--hairline-2);
        border-radius: var(--radius);
        box-shadow: var(--shadow-sm);
        display: flex;
        flex-direction: column;
        gap: 15px;
        min-height: 360px;
        transition: transform 0.16s ease, box-shadow 0.16s ease;
      }
      .product:hover {
        transform: translateY(-3px);
        box-shadow: var(--shadow);
      }
      .product.wide {
        grid-column: span 8;
      }
      @media (max-width: 960px) {
        .product,
        .product.wide {
          grid-column: span 12;
        }
      }
      .product .ph {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
      }
      .product .code {
        font-family: var(--ff-mono);
        font-size: 10px;
        letter-spacing: 0.04em;
        color: var(--muted);
      }
      .product .price {
        font-family: var(--ff-mono);
        font-size: 12px;
        color: var(--ink-2);
        text-align: right;
      }
      .product .price b {
        color: var(--ink);
        font-weight: 600;
      }
      .product h3 {
        margin: 0;
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: 24px;
        letter-spacing: -0.025em;
        line-height: 1.1;
      }
      .product p {
        margin: 0;
        color: var(--ink-2);
        font-size: 14.5px;
        line-height: 1.55;
      }
      .product ul {
        margin: 0;
        padding: 0;
        list-style: none;
        font-family: var(--ff-mono);
        font-size: 12px;
        color: var(--ink-2);
        display: flex;
        flex-direction: column;
      }
      .product ul li {
        padding: 8px 0;
        border-top: 1px solid var(--hairline-2);
        display: flex;
        justify-content: space-between;
        gap: 12px;
      }
      .product ul li:first-child {
        border-top: 0;
      }
      .product ul li .v {
        color: var(--muted);
      }
      .product .cta {
        margin-top: auto;
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: 13.5px;
        display: inline-flex;
        align-items: center;
        gap: 7px;
        color: var(--eu);
      }
      .product .cta .lucide {
        width: 15px;
        height: 15px;
      }

      /* Stack / infrastructure */
      .stack {
        display: grid;
        grid-template-columns: 1fr 1fr;
        border: 1px solid var(--hairline-2);
        border-radius: var(--radius);
        box-shadow: var(--shadow-sm);
        background: var(--card);
        overflow: hidden;
      }
      @media (max-width: 860px) {
        .stack {
          grid-template-columns: 1fr;
        }
      }
      .stack-left {
        padding: 32px;
        border-right: 1px solid var(--hairline-2);
      }
      .stack-right {
        padding: 32px;
        font-family: var(--ff-mono);
        font-size: 12px;
        background: var(--card-2);
        line-height: 1.75;
        color: var(--ink-2);
        white-space: pre;
        overflow-x: auto;
      }
      @media (max-width: 860px) {
        .stack-left {
          border-right: 0;
          border-bottom: 1px solid var(--hairline-2);
        }
      }
      .stack-left h3 {
        margin: 0 0 12px;
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: 24px;
        letter-spacing: -0.025em;
      }
      .stack-left p {
        margin: 0 0 20px;
        color: var(--ink-2);
        line-height: 1.55;
      }
      .stack-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        border-top: 1px solid var(--hairline-2);
      }
      .stack-grid .t {
        padding: 13px 12px 13px 0;
        border-bottom: 1px solid var(--hairline-2);
        border-right: 1px solid var(--hairline-2);
        font-family: var(--ff-mono);
        font-size: 12px;
        display: flex;
        justify-content: space-between;
        align-items: baseline;
      }
      .stack-grid .t:nth-child(2n) {
        border-right: 0;
        padding-left: 12px;
      }
      .stack-grid .t b {
        font-weight: 600;
        color: var(--ink);
      }
      .stack-grid .t span {
        color: var(--muted);
        font-size: 11px;
      }
      .code-comment {
        color: var(--muted);
      }
      .code-key {
        color: var(--eu);
      }
      .code-str {
        color: var(--green);
      }

      /* Specs table */
      .specs-wrap {
        border: 1px solid var(--hairline-2);
        border-radius: var(--radius);
        box-shadow: var(--shadow-sm);
        overflow: hidden;
        background: var(--card);
        margin-bottom: 4px;
      }
      .specs {
        width: 100%;
        border-collapse: collapse;
        font-family: var(--ff-mono);
        font-size: 13px;
      }
      .specs th,
      .specs td {
        text-align: left;
        padding: 14px 18px;
        border-bottom: 1px solid var(--hairline-2);
      }
      .specs tr:last-child td {
        border-bottom: 0;
      }
      .specs th {
        font-size: 11px;
        letter-spacing: 0.04em;
        color: var(--muted);
        font-weight: 500;
        text-transform: uppercase;
        background: var(--card-2);
      }
      .specs td b {
        font-weight: 600;
      }
      .specs td .muted {
        color: var(--muted);
      }
      .specs tbody tr:hover td {
        background: var(--panel-2);
      }

      /* Compliance */
      .cert-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 16px;
      }
      @media (max-width: 860px) {
        .cert-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      @media (max-width: 560px) {
        .cert-grid {
          grid-template-columns: 1fr;
        }
      }
      .cert {
        padding: 22px;
        background: var(--card);
        border: 1px solid var(--hairline-2);
        border-radius: var(--radius);
        box-shadow: var(--shadow-sm);
        display: flex;
        flex-direction: column;
        gap: 10px;
        min-height: 160px;
      }
      .cert .mark {
        width: 42px;
        height: 42px;
        border-radius: 12px;
        background: color-mix(in oklab, var(--eu) 12%, transparent);
        color: var(--eu);
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: var(--ff-mono);
        font-size: 13px;
        font-weight: 600;
      }
      .cert .n {
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: 16px;
        letter-spacing: -0.02em;
      }
      .cert .d {
        font-family: var(--ff-text);
        font-size: 13px;
        color: var(--muted);
        line-height: 1.5;
      }
      .cert .status {
        margin-top: auto;
        align-self: flex-start;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-family: var(--ff-mono);
        font-size: 10px;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        padding: 4px 10px;
        border-radius: var(--pill);
        color: var(--muted);
        background: var(--panel-2);
        white-space: nowrap;
      }
      .cert .status .lucide {
        width: 12px;
        height: 12px;
      }
      .cert .status.held {
        color: var(--green);
        background: color-mix(in oklab, var(--green) 13%, transparent);
      }
      .cert .status.progress {
        color: var(--amber);
        background: color-mix(in oklab, var(--amber) 13%, transparent);
      }
      .cert .status.planned {
        color: var(--eu);
        background: color-mix(in oklab, var(--eu) 13%, transparent);
      }

      /* Access steps */
      .access-steps {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 16px;
        margin-bottom: 28px;
      }
      @media (max-width: 860px) {
        .access-steps {
          grid-template-columns: 1fr;
        }
      }
      .access-step {
        padding: 24px;
        background: var(--card);
        border: 1px solid var(--hairline-2);
        border-radius: var(--radius);
        box-shadow: var(--shadow-sm);
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .access-step .label {
        display: flex;
        align-items: center;
        gap: 9px;
      }
      .access-step .label .lucide {
        width: 15px;
        height: 15px;
        color: var(--eu);
      }
      .access-step h3 {
        margin: 0;
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: 19px;
        letter-spacing: -0.02em;
        line-height: 1.15;
      }
      .access-step p {
        margin: 0;
        color: var(--ink-2);
        font-size: 14px;
        line-height: 1.55;
      }
      .access-step a.link {
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: 13px;
        margin-top: auto;
        align-self: flex-start;
        color: var(--eu);
        border: 0;
      }

      /* Estimator */
      .estimator {
        display: grid;
        grid-template-columns: 1.6fr 1fr;
        border: 1px solid var(--hairline-2);
        border-radius: var(--radius);
        box-shadow: var(--shadow-sm);
        background: var(--card);
        margin-bottom: 24px;
        overflow: hidden;
      }
      .est-controls {
        padding: 26px 28px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 22px 40px;
        border-right: 1px solid var(--hairline-2);
      }
      .est-row {
        display: flex;
        flex-direction: column;
        gap: 10px;
        justify-content: flex-end;
      }
      .est-key {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        font-family: var(--ff-text);
        font-size: 12px;
        font-weight: 500;
        color: var(--muted);
      }
      .est-key .ev {
        color: var(--ink);
        font-family: var(--ff-mono);
      }
      input[type='range'] {
        width: 100%;
        margin: 0;
        height: 22px;
        accent-color: var(--eu);
        -webkit-appearance: none;
        appearance: none;
        background: transparent;
      }
      input[type='range']::-webkit-slider-runnable-track {
        height: 4px;
        border-radius: var(--pill);
        background: var(--panel-2);
      }
      input[type='range']::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 18px;
        height: 18px;
        margin-top: -7px;
        border-radius: 50%;
        background: #fff;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25), 0 0 0 0.5px rgba(0, 0, 0, 0.04);
        cursor: pointer;
      }
      input[type='range']::-moz-range-track {
        height: 4px;
        border-radius: var(--pill);
        background: var(--panel-2);
      }
      input[type='range']::-moz-range-thumb {
        width: 18px;
        height: 18px;
        border: 0;
        border-radius: 50%;
        background: #fff;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
        cursor: pointer;
      }
      .est-total {
        padding: 26px 28px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        background: var(--card-2);
      }
      .est-total .sum {
        font-family: var(--ff-mono);
        font-size: 36px;
        font-weight: 600;
        color: var(--ink);
        letter-spacing: -0.02em;
        margin-top: auto;
      }
      .est-total .sum .permo {
        font-size: 14px;
        font-weight: 400;
        color: var(--muted);
        margin-left: 4px;
      }
      .est-total .note {
        font-family: var(--ff-mono);
        font-size: 11px;
        color: var(--muted);
        line-height: 1.55;
      }
      @media (max-width: 860px) {
        .estimator {
          grid-template-columns: 1fr;
        }
        .est-controls {
          border-right: 0;
          border-bottom: 1px solid var(--hairline-2);
          grid-template-columns: 1fr;
        }
      }
      .seg {
        display: flex;
        background: var(--panel-2);
        border-radius: var(--radius-xs);
        padding: 3px;
        gap: 2px;
      }
      .seg button {
        flex: 1;
        padding: 7px 10px;
        border-radius: 7px;
        background: transparent;
        font-family: var(--ff-text);
        font-size: 12px;
        font-weight: 500;
        color: var(--ink-2);
        transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
      }
      .seg button.on {
        background: var(--card);
        color: var(--ink);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 0 0 0.5px rgba(0, 0, 0, 0.04);
        font-weight: 600;
      }
      :host([gobright]) .seg button.on {
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 0 0 0.5px rgba(0, 0, 0, 0.04);
      }

      /* Big wordmark */
      .page {
        max-width: var(--maxw);
        margin: 0 auto;
        padding: 0 var(--pad-x);
      }
      .big-wordmark {
        font-family: var(--ff-display);
        font-weight: 600;
        font-size: clamp(80px, 18vw, 240px);
        letter-spacing: -0.05em;
        line-height: 0.85;
        color: var(--ink);
        padding: 48px 0 16px;
        user-select: none;
      }
      .big-wordmark .tld {
        color: var(--eu-brand);
      }

      /* Footer */
      .footer {
        border-top: 1px solid var(--hairline-2);
        background: var(--card);
      }
      .footer-inner {
        max-width: var(--maxw);
        margin: 0 auto;
        padding: 72px var(--pad-x) 32px;
        display: grid;
        grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
        gap: 48px;
      }
      @media (max-width: 860px) {
        .footer-inner {
          grid-template-columns: 1fr 1fr;
        }
      }
      .footer h4 {
        margin: 0 0 14px;
        font-family: var(--ff-mono);
        font-size: 11px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--muted);
        font-weight: 500;
      }
      .footer ul {
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 11px;
      }
      .footer a {
        color: var(--ink-2);
        font-size: 14px;
        transition: color 0.15s ease;
      }
      .footer a:hover {
        color: var(--ink);
      }
      .footer-bottom {
        max-width: var(--maxw);
        margin: 0 auto;
        padding: 20px var(--pad-x) 32px;
        border-top: 1px solid var(--hairline-2);
        display: flex;
        justify-content: space-between;
        gap: 24px;
        flex-wrap: wrap;
        font-family: var(--ff-mono);
        font-size: 11px;
        color: var(--muted);
        letter-spacing: 0.02em;
      }

      /* maplibre layout (global CSS does not pierce shadow DOM). The container
         rule must not set position/size: .hero-map-bg and .map-svg own those,
         and this later rule would win the cascade at equal specificity. */
      .maplibregl-map {
        overflow: hidden;
      }
      .maplibregl-canvas-container,
      .maplibregl-canvas {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
      .maplibregl-marker {
        position: absolute;
        top: 0;
        left: 0;
        will-change: transform;
      }
    `,
  ];

  private navigate(path: string): void {
    this.dispatchEvent(new CustomEvent('ceu-navigate', { detail: { path }, bubbles: true, composed: true }));
  }

  private async toggleTheme(): Promise<void> {
    const dt = await this.domtoolsPromise;
    dt.themeManager.toggleDarkBright();
    try {
      localStorage.setItem('ceu_theme', dt.themeManager.goBrightBoolean ? 'bright' : 'dark');
    } catch (e) {
      /* ignore */
    }
  }

  private sync(id: string, on: boolean): void {
    const root = this.shadowRoot;
    if (!root) return;
    root.querySelectorAll(`.map-marker[data-id="${id}"]`).forEach((n) => n.classList.toggle('active', on));
    root.querySelectorAll(`.dc-row[data-id="${id}"]`).forEach((n) => n.classList.toggle('active', on));
  }

  private get estTotal(): string {
    const total =
      this.estCores * CATALOG[this.estCoreType] +
      this.estMem * CATALOG.mem +
      this.estBlk * CATALOG.blk +
      this.estObj * CATALOG.obj;
    return '€' + total.toFixed(2);
  }

  public firstUpdated(): void {
    void this.initMaps();
  }

  protected themeChanged(goBright: boolean): void {
    const c = mapColors(goBright);
    [this.heroMap, this.footMap].forEach((map) => {
      if (!map || !map.getLayer) return;
      try {
        if (map.getLayer('bg')) map.setPaintProperty('bg', 'background-color', c.paper);
        if (map.getLayer('background')) map.setPaintProperty('background', 'background-color', c.paper);
        if (map.getLayer('country-fill')) map.setPaintProperty('country-fill', 'fill-color', c.paper2);
        if (map.getLayer('eu-fill')) map.setPaintProperty('eu-fill', 'fill-color', c.euFill);
        if (map.getLayer('country-line')) map.setPaintProperty('country-line', 'line-color', c.rule);
        if (map.getLayer('backbone-line')) map.setPaintProperty('backbone-line', 'line-color', c.eu);
      } catch (e) {
        /* map not ready */
      }
    });
  }

  public async disconnectedCallback(): Promise<void> {
    try {
      this.heroMap?.remove();
      this.footMap?.remove();
    } catch (e) {
      /* ignore */
    }
    await super.disconnectedCallback();
  }

  private async initMaps(): Promise<void> {
    const maplibregl = (window as any).maplibregl;
    if (!maplibregl || !this.shadowRoot) return;
    const c = mapColors(this.goBright);
    const backboneFrom = DCS[0];

    const makeStyle = (bgId: string) => ({
      version: 8,
      sources: {
        countries: { type: 'vector', url: 'https://demotiles.maplibre.org/tiles/tiles.json' },
      },
      layers: [
        { id: bgId, type: 'background', paint: { 'background-color': c.paper } },
        { id: 'country-fill', type: 'fill', source: 'countries', 'source-layer': 'countries', paint: { 'fill-color': c.paper2, 'fill-opacity': 1 } },
        { id: 'eu-fill', type: 'fill', source: 'countries', 'source-layer': 'countries', filter: ['in', 'ISO_A3', ...EU_ISO], paint: { 'fill-color': c.euFill, 'fill-opacity': 1 } },
        { id: 'country-line', type: 'line', source: 'countries', 'source-layer': 'countries', paint: { 'line-color': c.rule, 'line-width': 0.7, 'line-opacity': 0.9 } },
      ],
    });

    const addBackboneAndMarkers = (map: any, withHover: boolean) => {
      map.on('load', () => {
        const lines = DCS.filter((d) => d.id !== backboneFrom.id).map((d) => ({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [[backboneFrom.lng, backboneFrom.lat], [d.lng, d.lat]] },
        }));
        map.addSource('backbone', { type: 'geojson', data: { type: 'FeatureCollection', features: lines } });
        map.addLayer({
          id: 'backbone-line',
          type: 'line',
          source: 'backbone',
          paint: { 'line-color': mapColors(this.goBright).eu, 'line-width': 1, 'line-opacity': 0.3, 'line-dasharray': [3, 4] },
        });
        DCS.forEach((dc) => {
          const el = document.createElement('div');
          el.className = `map-marker ${dc.state}`;
          el.dataset.id = dc.id;
          el.innerHTML = `<span class="map-label">${dc.code}</span>`;
          new maplibregl.Marker({ element: el, anchor: 'center' }).setLngLat([dc.lng, dc.lat]).addTo(map);
          if (withHover) {
            el.addEventListener('mouseenter', () => this.sync(dc.id, true));
            el.addEventListener('mouseleave', () => this.sync(dc.id, false));
          }
        });
        // The theme can change while the style is still parsing; themeChanged's
        // getLayer guards skip silently then, so re-sync paints once loaded.
        this.themeChanged(this.goBright);
      });
    };

    const heroEl = this.shadowRoot.querySelector('#heroMap') as HTMLElement | null;
    if (heroEl) {
      this.heroMap = new maplibregl.Map({
        container: heroEl,
        style: makeStyle('bg'),
        bounds: [[-14, 33], [36, 63]],
        fitBoundsOptions: { padding: 0 },
        interactive: false,
        attributionControl: false,
      });
      addBackboneAndMarkers(this.heroMap, false);
    }

    const footEl = this.shadowRoot.querySelector('#euMap') as HTMLElement | null;
    if (footEl) {
      this.footMap = new maplibregl.Map({
        container: footEl,
        style: makeStyle('background'),
        bounds: [[-12, 34], [34, 62]],
        fitBoundsOptions: { padding: 32 },
        interactive: false,
        attributionControl: false,
      });
      addBackboneAndMarkers(this.footMap, true);
    }
  }

  public render(): TemplateResult {
    return html`
      <header class="nav">
        <div class="nav-inner">
          <a class="brand" href="/" @click=${(e: Event) => { e.preventDefault(); this.navigate('/'); }}>
            <span class="dot"></span><span>central</span><span class="tld">.eu</span>
          </a>
          <nav class="nav-links">
            <a href="#platform">Platform</a>
            <a href="#footprint">Footprint</a>
            <a href="#infrastructure">Infrastructure</a>
            <a href="#compliance">Compliance</a>
            <a href="#pricing">Pricing</a>
          </nav>
          <div class="nav-right">
            <span class="status mono"><span class="status-text">all systems nominal</span></span>
            <button class="nav-toggle" @click=${() => this.toggleTheme()} title="Toggle light / dark">
              ${this.goBright ? icon('Moon') : icon('Sun')}
            </button>
            <a class="btn" href="/console" @click=${(e: Event) => { e.preventDefault(); this.navigate('/console'); }}>
              Get a quote ${icon('ArrowRight')}
            </a>
          </div>
        </div>
      </header>

      <section class="hero" id="top">
        <div class="hero-grid">
          <div class="label" style="margin-bottom: 20px;">01 — Physical infrastructure · Established 2021 · Bremen, DE</div>
          <h1>EU metal.<br />Distributed<br /><span class="stripe">containers.</span></h1>
          <p class="hero-lede">
            An open-source cloud for the European Union — hardware we own, a stack anyone can
            audit, operated under EU law. We're near full: request a quote for allocation review
            as we build more capacity.
          </p>
          <div class="hero-cta">
            <a class="btn" href="/console" @click=${(e: Event) => { e.preventDefault(); this.navigate('/console'); }}>Get a quote ${icon('ArrowRight')}</a>
            <a class="btn ghost" href="#pricing">How access works</a>
          </div>
          <div class="hero-meta">
            <span class="kv"><span class="k">DC—</span> <span class="v">BRE1 (own)</span></span>
            <span class="kv"><span class="k">NET—</span> <span class="v">EU fiber</span></span>
            <span class="kv"><span class="k">HQ—</span> <span class="v">Bremen, DE</span></span>
            <span class="kv"><span class="k">ENTITY—</span> <span class="v">Task VC GmbH</span></span>
          </div>
        </div>
        <div class="hero-map-bg" id="heroMap" aria-hidden="true"></div>
      </section>

      <div class="ticker" aria-hidden="true">
        <div class="ticker-track">
          ${[0, 1, 2].map(() =>
            TICKER_ITEMS.map(
              (txt, i) => html`<span class="${i % 5 === 2 ? 'on' : ''}">${txt}</span><span class="sep">·</span>`,
            ),
          )}
        </div>
      </div>

      <section class="section" id="values">
        <div class="section-inner">
          <div class="section-head">
            <div class="index">02 — Doctrine</div>
            <div>
              <h2>Three commitments,<br />published and kept.</h2>
              <p class="lede" style="margin-top:18px;">
                Not marketing language. Constraints we publish, hold ourselves to, and review
                annually in the open. If we break them, we cease to be central.eu.
              </p>
            </div>
          </div>
        </div>
        <div class="values">
          <div class="value">
            <span class="ic-tile">${icon('Building2')}</span>
            <div class="idx">I.</div>
            <h3>Bremen is ours. New regions start rented.</h3>
            <p>Our Bremen facility is owned outright — building, racks, metal. New EU regions launch on rented infrastructure, labeled as such, and are converted to own presence over time. Everything runs the open-source <a class="link" href="https://compute.cloud" target="_blank">compute.cloud</a> platform — maintained by <a class="link" href="https://foss.global" target="_blank">foss.global</a>, auditable by anyone.</p>
          </div>
          <div class="value">
            <span class="ic-tile">${icon('Boxes')}</span>
            <div class="idx">II.</div>
            <h3>Containers, distributed across EU nodes.</h3>
            <p>Schedule workloads across our growing cluster of EU nodes. Automatic failover, latency-aware placement, no egress fees between nodes. Your containers stay in the union.</p>
          </div>
          <div class="value">
            <span class="ic-tile">${icon('TrendingUp')}</span>
            <div class="idx">III.</div>
            <h3>Honest about where we are.</h3>
            <p>We are starting small — a handful of servers, ordinary fiber connectivity, no private backbone yet. We will say exactly what we have, what we don't, and what is coming. No vaporware.</p>
          </div>
        </div>
      </section>

      <section class="section" id="footprint">
        <div class="section-inner">
          <div class="section-head">
            <div class="index">03 — Footprint</div>
            <div>
              <h2>One own site.<br />More in build.</h2>
              <p class="lede" style="margin-top:18px;">
                Our own facility in Bremen, DE is live. Additional EU nodes in Frankfurt,
                Amsterdam, Paris and Stockholm are planned — launching on rented infrastructure
                until converted to own presence. Nodes talk over an encrypted mesh on ordinary fiber.
              </p>
            </div>
          </div>
          <div class="map-wrap">
            <div class="map-svg" id="euMap"></div>
            <aside class="map-side">
              <h3>Regions</h3>
              <div class="summary">1 own site live · 5 planned, rented first · encrypted mesh between nodes</div>
              <div class="dc-list">
                ${DCS.map(
                  (d) => html`
                    <div
                      class="dc-row"
                      data-id=${d.id}
                      data-state=${d.state}
                      @mouseenter=${() => this.sync(d.id, true)}
                      @mouseleave=${() => this.sync(d.id, false)}
                    >
                      <span class="code">${d.code}</span>
                      <span class="name">${d.city}, ${d.country}<span class="sub"> ${d.tier}</span></span>
                      <span class="pill ${d.state === 'build' ? 'build' : ''}">${d.state === 'live' ? 'LIVE' : d.tier}</span>
                    </div>
                  `,
                )}
              </div>
              <div class="summary" style="margin-top:auto;">
                Ping values are rolling 30-day medians from the BRE1 anchor.<br /><br />
                No IXP presence yet — connectivity is EU carrier fiber, with ingress fronted by EU cloud partners.
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section class="section" id="platform">
        <div class="section-inner">
          <div class="section-head">
            <div class="index">04 — Platform</div>
            <div>
              <h2>Containers on<br />owned EU metal.</h2>
              <p class="lede" style="margin-top:18px;">
                We run your container workloads on hardware we own, in facilities we control,
                connected over an encrypted mesh on public fiber. No hyperscaler underneath.
                No US entity in the stack. Priced simply — per core, per GB, per month.
              </p>
            </div>
          </div>
        </div>
        <div class="products">
          <article class="product">
            <div class="ph">
              <span class="ic-tile">${icon('Box')}</span>
              <span class="price">from €<b>7.50</b>/core·mo</span>
            </div>
            <span class="code">P/01 · CONTAINER WORKLOADS</span>
            <h3>Distributed containers</h3>
            <p>OCI-compatible containers scheduled across our EU node cluster. Latency-aware placement, automatic failover between nodes, persistent volumes, bring your own registry.</p>
            <ul>
              <li><span>Runtime</span><span class="v">containerd · OCI</span></li>
              <li><span>Scheduling</span><span class="v">latency-aware, EU-pinnable</span></li>
              <li><span>Volumes</span><span class="v">persistent, replicated</span></li>
              <li><span>Egress between nodes</span><span class="v">€0</span></li>
            </ul>
            <a class="cta" href="/console" @click=${(e: Event) => { e.preventDefault(); this.navigate('/console'); }}>Get a quote ${icon('ArrowRight')}</a>
          </article>
          <article class="product">
            <div class="ph">
              <span class="ic-tile">${icon('Server')}</span>
              <span class="price">from €<b>990</b>/mo</span>
            </div>
            <span class="code">P/02 · BARE METAL ACCESS</span>
            <h3>Dedicated nodes</h3>
            <p>Reserve a physical node in our cluster for single-tenant workloads. Full BMC access, measured boot, your choice of OS or bring your own container runtime.</p>
            <ul>
              <li><span>Hardware</span><span class="v">AMD EPYC · Ampere Altra</span></li>
              <li><span>Network</span><span class="v">10 GbE uplink per node</span></li>
              <li><span>Access</span><span class="v">IPMI / BMC, SSH</span></li>
              <li><span>Term</span><span class="v">monthly · 7-day minimum</span></li>
            </ul>
            <a class="cta" href="/console" @click=${(e: Event) => { e.preventDefault(); this.navigate('/console'); }}>Reserve a node ${icon('ArrowRight')}</a>
          </article>
          <article class="product">
            <div class="ph">
              <span class="ic-tile">${icon('Share2')}</span>
              <span class="price">included</span>
            </div>
            <span class="code">P/03 · NETWORK</span>
            <h3>Encrypted EU mesh</h3>
            <p>Your workloads talk to each other over an encrypted WireGuard mesh running on ordinary fiber internet from EU carriers. Public ingress and IP space are allocated from EU cloud partners and routed to our hardware. No private backbone, no IXP presence yet — we'll tell you when there is.</p>
            <ul>
              <li><span>Coverage</span><span class="v">all CEU nodes</span></li>
              <li><span>Transport</span><span class="v">fiber · WireGuard mesh</span></li>
              <li><span>Ingress</span><span class="v">EU cloud partners → BRE1</span></li>
              <li><span>Inter-node egress</span><span class="v">€0</span></li>
            </ul>
            <a class="cta" href="/console" @click=${(e: Event) => { e.preventDefault(); this.navigate('/console'); }}>Learn more ${icon('ArrowRight')}</a>
          </article>
          <article class="product wide">
            <div class="ph">
              <span class="ic-tile">${icon('Database')}</span>
              <span class="price">block €<b>0.11</b>/GiB·mo · object €<b>0.018</b>/GiB·mo · inter-node egress <b>€0</b></span>
            </div>
            <span class="code">P/04 · STORAGE</span>
            <h3>Persistent storage</h3>
            <p>Block volumes attached to your containers or bare-metal nodes, replicated within our Bremen facility. S3-compatible object storage for unstructured data. All data stays on EU soil — residency enforced at the storage layer, not just contractually.</p>
            <ul style="display:grid; grid-template-columns: repeat(2, 1fr); column-gap: 40px;">
              <li><span>Block</span><span class="v">NVMe-backed, replicated ×2</span></li>
              <li><span>Object API</span><span class="v">S3-compatible</span></li>
              <li><span>Residency</span><span class="v">EU-only, enforced</span></li>
              <li><span>Snapshots</span><span class="v">included</span></li>
              <li><span>Encryption</span><span class="v">at-rest, AES-256</span></li>
              <li><span>Egress</span><span class="v">€0 between CEU nodes</span></li>
            </ul>
            <a class="cta" href="/console" @click=${(e: Event) => { e.preventDefault(); this.navigate('/console'); }}>Explore storage ${icon('ArrowRight')}</a>
          </article>
        </div>
      </section>

      <section class="section" id="infrastructure">
        <div class="section-inner">
          <div class="section-head">
            <div class="index">05 — Infrastructure</div>
            <div>
              <h2>Our own facility.<br />Bremen, DE.</h2>
              <p class="lede" style="margin-top:18px;">
                Our primary site is a purpose-built facility in Bremen, Germany. Owned and
                operated by central.eu — not leased, not shared. The stack is driven by open
                source: <a class="link" href="https://compute.cloud" target="_blank">compute.cloud</a>,
                <a class="link" href="https://serve.zone" target="_blank">serve.zone</a> and
                <a class="link" href="https://modelgrid.com" target="_blank">modelgrid.com</a> —
                all available on <a class="link" href="https://foss.global" target="_blank">foss.global</a>.
              </p>
            </div>
          </div>
          <div class="stack">
            <div class="stack-left">
              <h3>Physical layer, no surprises.</h3>
              <p>We're starting honest: a cluster of high-density servers, redundant power and cooling, and ordinary fiber from EU carriers. Public ingress is fronted by EU cloud partners and routed to our hardware. Private interconnect and additional sites are on the roadmap, not the invoice.</p>
              <div class="stack-grid">
                <div class="t"><b>Location</b> <span>Bremen, DE</span></div>
                <div class="t"><b>Ownership</b> <span>CEU-owned</span></div>
                <div class="t"><b>Power</b> <span>redundant, diesel backup</span></div>
                <div class="t"><b>Cooling</b> <span>N+1, PUE &lt;1.4</span></div>
                <div class="t"><b>Uplink</b> <span>fiber, EU carriers</span></div>
                <div class="t"><b>Ingress</b> <span>EU partners → BRE1</span></div>
                <div class="t"><b>Hardware</b> <span>EPYC · Ampere Altra</span></div>
                <div class="t"><b>Storage</b> <span>NVMe, Ceph replicated</span></div>
                <div class="t"><b>Runtime</b> <span>containerd, Linux 6.x</span></div>
                <div class="t"><b>Renewable</b> <span>100% matched</span></div>
              </div>
            </div>
            <pre class="stack-right"><span class="code-comment"># Deploy a container to the CEU cluster.</span>
<span class="code-comment"># Pinned to EU nodes. Persistent volume attached.</span>

$ central run \\
    <span class="code-key">--image</span>   <span class="code-str">registry.example.com/app:v1.4</span> \\
    <span class="code-key">--cores</span>   <span class="code-str">4</span> \\
    <span class="code-key">--memory</span>  <span class="code-str">8Gi</span> \\
    <span class="code-key">--volume</span>  <span class="code-str">data:/data:50Gi</span> \\
    <span class="code-key">--region</span>  <span class="code-str">eu</span> \\
    <span class="code-key">--replicas</span> <span class="code-str">2</span>

<span class="code-comment">→ pulling image ················· ok</span>
<span class="code-comment">→ scheduling: bre1 (primary) ··· ok</span>
<span class="code-comment">→ scheduling: bre1 (replica) ··· ok</span>
<span class="code-comment">→ volume mounted ················ ok</span>
<span class="code-comment">→ residency: DE · EU-only ······ attested</span>
<span class="code-comment">→ endpoint: app.ceu.run ········ live</span></pre>
          </div>
        </div>
      </section>

      <section class="section" id="compliance">
        <div class="section-inner">
          <div class="section-head">
            <div class="index">06 — Compliance</div>
            <div>
              <h2>GDPR today.<br />The rest, honestly in progress.</h2>
              <p class="lede" style="margin-top:18px;">
                One framework is fulfilled today: GDPR. Everything else below is in preparation
                or arrives with future sites — and we won't claim it before it's real.
              </p>
            </div>
          </div>
          <div class="cert-grid">
            <div class="cert">
              <div class="mark">GD</div>
              <div class="n">GDPR</div>
              <div class="d">Regulation (EU) 2016/679. Controller-processor terms in every contract. DPIA templates on request.</div>
              <div class="status held">${icon('Check')} In force</div>
            </div>
            <div class="cert">
              <div class="mark">NI</div>
              <div class="n">NIS2</div>
              <div class="d">Preparation underway: registration and 24h incident-reporting procedures to national CSIRTs.</div>
              <div class="status progress">${icon('Clock')} In progress</div>
            </div>
            <div class="cert">
              <div class="mark">DO</div>
              <div class="n">DORA</div>
              <div class="d">Register of Information entries for BRE1 and ICT third-party contract annexes in preparation.</div>
              <div class="status progress">${icon('Clock')} In progress</div>
            </div>
            <div class="cert">
              <div class="mark">27</div>
              <div class="n">ISO 27001</div>
              <div class="d">SoA in preparation. Certification targeted for BRE1 — no audit booked yet.</div>
              <div class="status progress">${icon('Clock')} In progress</div>
            </div>
            <div class="cert">
              <div class="mark">C5</div>
              <div class="n">BSI C5:2020</div>
              <div class="d">Type 1 targeted once FRA1 is live; Type 2 after a full reporting period.</div>
              <div class="status planned">${icon('CalendarClock')} With FRA1</div>
            </div>
            <div class="cert">
              <div class="mark">SN</div>
              <div class="n">SecNumCloud</div>
              <div class="d">ANSSI qualification pursued alongside the PAR1 build. EU-only legal exposure by design.</div>
              <div class="status planned">${icon('CalendarClock')} With PAR1</div>
            </div>
          </div>
        </div>
      </section>

      <section class="section" id="pricing">
        <div class="section-inner">
          <div class="section-head">
            <div class="index">07 — Pricing</div>
            <div>
              <h2>Simple pricing.<br />No hidden fees.</h2>
              <p class="lede" style="margin-top:18px;">
                The rates below are our general-availability list. While BRE1 is near full, live
                quotes reflect current demand — request a quote for allocation review and a seven-day pricing review.
              </p>
            </div>
          </div>
          <div class="access-steps">
            <div class="access-step">
              <div class="label">${icon('Gauge')} 01 — Near full</div>
              <h3>BRE1 is running near full.</h3>
              <p>Our only live, owned site is near capacity. We're building more EU nodes — deliberately, and we say exactly where we are.</p>
            </div>
            <div class="access-step">
              <div class="label">${icon('FileText')} 02 — Get a quote</div>
              <h3>Compose a stack, get a quote.</h3>
              <p>Build your bill of materials in the console. While we're near full, quotes reflect current demand.</p>
              <a class="link" href="/console" @click=${(e: Event) => { e.preventDefault(); this.navigate('/console'); }}>Open the console →</a>
            </div>
            <div class="access-step">
              <div class="label">${icon('ListOrdered')} 03 — Allocation review</div>
              <h3>We review capacity.</h3>
              <p>An engineer reviews the requested allocation against available capacity. The non-binding pricing snapshot has a seven-day review window.</p>
            </div>
          </div>

          <div class="estimator">
            <div class="est-controls">
              <div class="est-row">
                <div class="est-key"><label>Cores</label><span class="ev">${this.estCores} vCPU</span></div>
                <input type="range" min="1" max="64" step="1" .value=${String(this.estCores)} @input=${(e: Event) => (this.estCores = +(e.target as HTMLInputElement).value)} />
              </div>
              <div class="est-row">
                <div class="est-key"><label>Memory</label><span class="ev">${this.estMem} GiB</span></div>
                <input type="range" min="1" max="256" step="1" .value=${String(this.estMem)} @input=${(e: Event) => (this.estMem = +(e.target as HTMLInputElement).value)} />
              </div>
              <div class="est-row">
                <div class="est-key"><label>Block storage</label><span class="ev">${this.estBlk} GiB</span></div>
                <input type="range" min="0" max="2000" step="10" .value=${String(this.estBlk)} @input=${(e: Event) => (this.estBlk = +(e.target as HTMLInputElement).value)} />
              </div>
              <div class="est-row">
                <div class="est-key"><label>Object storage</label><span class="ev">${this.estObj} GiB</span></div>
                <input type="range" min="0" max="5000" step="50" .value=${String(this.estObj)} @input=${(e: Event) => (this.estObj = +(e.target as HTMLInputElement).value)} />
              </div>
              <div class="est-row">
                <div class="est-key"><label>Core type</label></div>
                <div class="seg">
                  <button class=${this.estCoreType === 'shared' ? 'on' : ''} @click=${() => (this.estCoreType = 'shared')}>Shared</button>
                  <button class=${this.estCoreType === 'dedicated' ? 'on' : ''} @click=${() => (this.estCoreType = 'dedicated')}>Dedicated</button>
                </div>
              </div>
            </div>
            <div class="est-total">
              <div class="label">GA list estimate</div>
              <div class="sum mono">${this.estTotal}<span class="permo">/mo</span></div>
              <div class="note mono">excl. VAT · 7-day min. · GA list rate, before current demand</div>
              <a class="btn" href="/console" @click=${(e: Event) => { e.preventDefault(); this.navigate('/console'); }} style="margin-top:10px; align-self:flex-start;">Get a demand quote ${icon('ArrowRight')}</a>
            </div>
          </div>

          <div class="specs-wrap">
            <table class="specs">
              <thead>
                <tr><th>Resource</th><th>Unit</th><th>Notes</th><th>€/mo</th></tr>
              </thead>
              <tbody>
                <tr><td><b>Shared core</b> <span class="muted">· container</span></td><td>per vCPU</td><td>burstable, EPYC 9004</td><td>€7.50</td></tr>
                <tr><td><b>Dedicated core</b> <span class="muted">· container</span></td><td>per vCPU</td><td>pinned SMT thread</td><td>€15.00</td></tr>
                <tr><td><b>Memory</b></td><td>per GiB</td><td>ECC DDR5 · AI-driven shortage</td><td>€2.20</td></tr>
                <tr><td><b>Block storage</b> <span class="muted">· NVMe</span></td><td>per GiB</td><td>replicated ×2, snapshots incl.</td><td>€0.11</td></tr>
                <tr><td><b>Object storage</b></td><td>per GiB</td><td>S3-compatible, EU-only</td><td>€0.018</td></tr>
                <tr><td><b>Public IPv4</b></td><td>per address</td><td>via EU cloud partners, static</td><td>€2.50</td></tr>
                <tr><td><b>Dedicated node</b> <span class="muted">· EPYC 9654</span></td><td>per node/mo</td><td>96c / 384 GiB / 4×NVMe</td><td>from €990</td></tr>
                <tr><td><b>Inter-node egress</b></td><td>—</td><td>between all CEU nodes</td><td>€0</td></tr>
              </tbody>
            </table>
          </div>
          <p class="mono" style="font-size:12px; color: var(--muted); margin-top:16px;">
            Prices exclude VAT. List rates reflect the 2026 hardware market — server DRAM, NVMe and accelerators remain supply-constrained by AI demand. Reverse charge applies for valid EU VAT IDs. Monthly rates, billed pro-rata — newly provisioned resources are billed for a minimum of 7 days.
          </p>
        </div>
      </section>

      <section class="section">
        <div class="section-inner" style="padding-top:48px; padding-bottom:0;">
          <div class="label">08 — Colophon</div>
        </div>
        <div class="page"><div class="big-wordmark">central<span class="tld">.eu</span></div></div>
        <div class="section-inner" style="padding-top:32px;">
          <div style="display:grid; grid-template-columns: 1fr 1fr 1fr; gap:48px; border-top:1px solid var(--rule); padding-top:32px;">
            <div>
              <div class="label" style="margin-bottom:12px;">Legal form</div>
              <p style="margin:0; color: var(--ink-2); font-size:14px;">central.eu is operated by Task Venture Capital GmbH, registered in Bremen, DE. Own DC in Bremen, Germany. Legal information at <a class="link" href="https://legal.task.vc" target="_blank">legal.task.vc</a>.</p>
            </div>
            <div>
              <div class="label" style="margin-bottom:12px;">Ownership &amp; stack</div>
              <p style="margin:0; color: var(--ink-2); font-size:14px;">Operated by <a class="link" href="https://task.vc" target="_blank">Task Venture Capital GmbH</a>, which also funds <a class="link" href="https://foss.global" target="_blank">foss.global</a> — home of the open-source platforms central.eu runs on.</p>
            </div>
            <div>
              <div class="label" style="margin-bottom:12px;">Contact</div>
              <p style="margin:0; color: var(--ink-2); font-size:14px;">Sales — <a class="link" href="mailto:hello@central.eu">hello@central.eu</a><br />Abuse — <a class="link" href="mailto:abuse@central.eu">abuse@central.eu</a><br />Security — <a class="link" href="mailto:security@central.eu">security@central.eu</a></p>
            </div>
          </div>
        </div>
      </section>

      <footer class="footer">
        <div class="footer-inner">
          <div>
            <a class="brand" href="/" @click=${(e: Event) => { e.preventDefault(); this.navigate('/'); }} style="margin-bottom:18px;">
              <span class="dot"></span><span>central</span><span class="tld">.eu</span>
            </a>
            <p style="margin:0; color: var(--muted); font-size:13px; max-width:38ch;">Physical infrastructure and distributed container workloads. Own DC in Bremen, DE. Operated by Task Venture Capital GmbH.</p>
          </div>
          <div><h4>Services</h4><ul><li><a href="#platform">Container workloads</a></li><li><a href="#platform">Bare metal nodes</a></li><li><a href="#platform">Block storage</a></li><li><a href="#platform">Networking</a></li></ul></div>
          <div><h4>Infrastructure</h4><ul><li><a href="#infrastructure">Bremen DC</a></li><li><a href="#footprint">Footprint</a></li><li><a href="https://foss.global" target="_blank">Open source</a></li></ul></div>
          <div><h4>Compliance</h4><ul><li><a href="#compliance">GDPR</a></li><li><a href="#compliance">DORA</a></li><li><a href="#compliance">NIS2</a></li><li><a href="#compliance">ISO 27001</a></li></ul></div>
          <div><h4>Company</h4><ul><li><a href="https://task.vc" target="_blank">Operator</a></li><li><a href="https://legal.task.vc" target="_blank">Legal</a></li></ul></div>
        </div>
        <div class="footer-bottom">
          <span>© 2026 Task Venture Capital GmbH · Amtsgericht Bremen · HRB 35230 HB · <a class="link" href="https://legal.task.vc" target="_blank">legal.task.vc</a></span>
          <span>v2026.04 · built in the union</span>
        </div>
      </footer>
    `;
  }
}
